/**
 * Support Management Page
 * Admin portal page to manage support tickets and customer support
 */

import React, { useEffect, useState, type ComponentType } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../components/shared/DataTable';
import { Modal } from '../../components/shared/Modal';
import { Pagination } from '../../components/shared/Pagination';
import { ExportDataModal } from '../../components/shared/ExportDataModal';
import { useToast } from '../../components/shared/Toast';
import { SupportTicketForm } from './SupportTicketForm';
import {
  MdAdd as MdAddRaw,
  MdSearch as MdSearchRaw,
  MdFilterList as MdFilterListRaw,
  MdDownload as MdDownloadRaw,
  MdSupport as MdSupportRaw,
  MdCheckCircle as MdCheckCircleRaw,
  MdWarning as MdWarningRaw,
  MdSchedule as MdScheduleRaw,
  MdRefresh as MdRefreshRaw,
} from 'react-icons/md';
import { useDebouncedSearch } from '@euroasiann/shared';
import { PageHeader, ClearFiltersButton, erpPageBtnPrimaryClass, erpPageBtnSecondaryClass, erpStatGridClasses, erpStatCardClass, erpStatCardLabelClass, erpStatCardValueClass, erpStatCardIconClass, erpSearchToolbarClass, erpSearchToolbarFieldClass, erpSearchToolbarInputClass, erpSearchToolbarFiltersClass, erpSearchToolbarFiltersLabelClass, erpSearchToolbarSelectClass, PageSkeleton } from '@euroasiann/shared/ui';
import { cn } from '../../lib/utils';
import { downloadFromResponse } from '../../utils/download';

type IconComp = ComponentType<{ className?: string }>;
const MdAdd = MdAddRaw as IconComp;
const MdSearch = MdSearchRaw as IconComp;
const MdFilterList = MdFilterListRaw as IconComp;
const MdDownload = MdDownloadRaw as IconComp;
const MdSupport = MdSupportRaw as IconComp;
const MdCheckCircle = MdCheckCircleRaw as IconComp;
const MdWarning = MdWarningRaw as IconComp;
const MdSchedule = MdScheduleRaw as IconComp;
const MdRefresh = MdRefreshRaw as IconComp;

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const DEFAULT_LIMIT = 10;

interface SupportTicket {
  _id: string;
  serialNo: number;
  ticketNumber: string;
  subject: string;
  description: string;
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  organizationId?: string;
  organizationName?: string;
  createdBy?: string;
  assignedTo?: string;
  createdAt?: string;
  updatedAt?: string;
  resolvedAt?: string;
}

function orgNameFromTicket(ticket: Record<string, unknown>): string {
  if (typeof ticket.organizationName === 'string' && ticket.organizationName.trim()) {
    return ticket.organizationName;
  }
  const org = ticket.organizationId;
  if (org && typeof org === 'object' && org !== null && 'name' in org) {
    const name = (org as { name?: string }).name;
    if (name) return name;
  }
  return 'N/A';
}

function mapApiTicket(
  ticket: Record<string, unknown>,
  index: number,
  page: number,
  limit: number
): SupportTicket {
  const status = String(ticket.status || 'open');
  const priority = String(ticket.priority || 'low');
  return {
    _id: String(ticket._id || ''),
    serialNo: (page - 1) * limit + index + 1,
    ticketNumber: String(ticket.ticketNumber || ''),
    subject: String(ticket.subject || ticket.title || ''),
    description: String(ticket.description || ''),
    status: status as SupportTicket['status'],
    priority: (priority === 'critical' ? 'urgent' : priority) as SupportTicket['priority'],
    organizationName: orgNameFromTicket(ticket),
    createdBy: typeof ticket.createdBy === 'string' ? ticket.createdBy : undefined,
    assignedTo: typeof ticket.assignedTo === 'string' ? ticket.assignedTo : undefined,
    createdAt: ticket.createdAt ? String(ticket.createdAt) : undefined,
    updatedAt: ticket.updatedAt ? String(ticket.updatedAt) : undefined,
    resolvedAt: ticket.resolvedAt ? String(ticket.resolvedAt) : undefined,
  };
}

export function SupportPage() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTicket, setEditingTicket] = useState<SupportTicket | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const {
    inputValue: searchQuery,
    submittedValue: submittedSearch,
    setInputValue: setSearchQuery,
    clear: clearSearch,
    onKeyDown: onSearchKeyDown,
  } = useDebouncedSearch();
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [filterStatus, filterPriority, submittedSearch, limit]);

  const listQueryKey = ['support-tickets', page, limit, filterStatus, filterPriority, submittedSearch] as const;

  const buildTicketParams = (includePaging: boolean) => {
    const params = new URLSearchParams();
    if (includePaging) {
      params.set('page', String(page));
      params.set('limit', String(limit));
    }
    if (filterStatus !== 'all') {
      params.set('status', filterStatus === 'in-progress' ? 'in_progress' : filterStatus);
    }
    if (filterPriority !== 'all') {
      params.set('priority', filterPriority === 'urgent' ? 'critical' : filterPriority);
    }
    if (submittedSearch) {
      params.set('search', submittedSearch);
    }
    return params;
  };

  const { data: ticketsResult, isLoading } = useQuery({
    queryKey: listQueryKey,
    queryFn: async ({ signal }) => {
      const params = buildTicketParams(true);
      const response = await fetch(`${API_URL}/api/v1/admin/support-tickets?${params}`, {
        signal,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to load support tickets');
      }

      const json = await response.json();
      const raw = json.data?.data ?? json.data ?? json.tickets;
      const tickets = (Array.isArray(raw) ? raw : []) as Record<string, unknown>[];
      const pagination = json.pagination || {};
      const currentPage = Number(pagination.page ?? json.page ?? page) || page;
      const currentLimit = Number(pagination.limit ?? json.limit ?? limit) || limit;
      const total = Number(pagination.total ?? json.total ?? tickets.length) || 0;
      const totalPages = Number(pagination.totalPages ?? json.totalPages ?? Math.max(1, Math.ceil(total / currentLimit))) || 1;
      const summary = json.summary || {
        total,
        open: tickets.filter((t) => t.status === 'open').length,
        inProgress: tickets.filter((t) => String(t.status).includes('progress')).length,
        resolved: tickets.filter((t) => t.status === 'resolved' || t.status === 'closed').length,
        urgent: tickets.filter((t) => t.priority === 'urgent' || t.priority === 'critical').length,
      };

      return {
        tickets: tickets.map((ticket, index) => mapApiTicket(ticket, index, currentPage, currentLimit)),
        total,
        page: currentPage,
        limit: currentLimit,
        totalPages,
        summary,
      };
    },
  });

  const tickets = ticketsResult?.tickets || [];
  const totalTickets = ticketsResult?.total ?? 0;
  const totalPages = ticketsResult?.totalPages ?? 1;

  const deleteMutation = useMutation({
    mutationFn: async (ticketId: string) => {
      const response = await fetch(`${API_URL}/api/v1/admin/support-tickets/${ticketId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete ticket');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
      showToast('Ticket deleted successfully!', 'success');
    },
    onError: (error: Error) => {
      showToast(`Failed to delete: ${error.message}`, 'error');
    },
  });

  const handleCreate = () => {
    setEditingTicket(null);
    setIsModalOpen(true);
  };

  const handleEdit = (ticket: SupportTicket) => {
    setEditingTicket(ticket);
    setIsModalOpen(true);
  };

  const handleDelete = (ticket: SupportTicket) => {
    if (window.confirm(`Are you sure you want to delete ticket "${ticket.subject}"?`)) {
      deleteMutation.mutate(ticket._id);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setEditingTicket(null);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
    showToast(
      editingTicket ? 'Ticket updated successfully!' : 'Ticket created successfully!',
      'success'
    );
    handleClose();
  };

  const handleExport = async (format: 'excel' | 'pdf') => {
    const params = buildTicketParams(false);
    params.set('format', format);
    const response = await fetch(`${API_URL}/api/v1/admin/support-tickets/export?${params}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to export tickets');
    }

    const fallback = `support-tickets.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
    await downloadFromResponse(response, fallback, format === 'pdf' ? '.pdf' : '.xlsx');
    showToast(`Tickets exported as ${format === 'pdf' ? 'PDF' : 'Excel'}`, 'success');
  };

  const getStatusBadge = (status: string) => {
    const normalized = (status || '').replace('_', '-');
    const styles = {
      open: 'bg-blue-100 text-[hsl(var(--foreground))] font-semibold dark:bg-blue-900/50 ring-1 ring-blue-200 dark:ring-blue-800',
      'in-progress': 'bg-amber-100 text-[hsl(var(--foreground))] font-semibold dark:bg-amber-900/50 ring-1 ring-amber-200 dark:ring-amber-800',
      resolved: 'bg-emerald-100 text-[hsl(var(--foreground))] font-semibold dark:bg-emerald-900/50 ring-1 ring-emerald-200 dark:ring-emerald-800',
      closed: 'bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))] ring-1 ring-[hsl(var(--border))]',
    };
    return (
      <span className={cn('px-2.5 py-0.5 inline-flex text-xs leading-5 font-bold rounded-full', styles[normalized as keyof typeof styles] || styles.open)}>
        {(status || '').charAt(0).toUpperCase() + (status || '').slice(1).replace(/[-_]/g, ' ')}
      </span>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const styles = {
      low: 'bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))]',
      medium: 'bg-blue-100 text-[hsl(var(--foreground))] font-semibold dark:bg-blue-900/50',
      high: 'bg-orange-100 text-[hsl(var(--foreground))] font-semibold dark:bg-orange-900/50',
      urgent: 'bg-red-100 text-[hsl(var(--foreground))] font-semibold dark:bg-red-900/50',
    };
    return (
      <span className={cn('px-2.5 py-0.5 inline-flex text-xs leading-5 font-bold rounded-full', styles[priority as keyof typeof styles] || styles.low)}>
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </span>
    );
  };

  const columns = [
    {
      key: 'serialNo',
      header: 'S.No',
      className: 'w-14',
      render: (ticket: SupportTicket) => (
        <span className="font-semibold text-[hsl(var(--foreground))]">{ticket.serialNo}</span>
      ),
    },
    {
      key: 'subject',
      header: 'Subject',
      className: 'w-[16%]',
      render: (ticket: SupportTicket) => (
        <span className="font-semibold text-[hsl(var(--foreground))] line-clamp-1">{ticket.subject}</span>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      className: 'w-[22%]',
      render: (ticket: SupportTicket) => (
        <span className="text-[hsl(var(--muted-foreground))] line-clamp-2" title={ticket.description}>
          {ticket.description || '—'}
        </span>
      ),
    },
    {
      key: 'organization',
      header: 'Organization',
      className: 'w-[16%]',
      render: (ticket: SupportTicket) => (
        <span className="text-[hsl(var(--muted-foreground))] line-clamp-1">
          {ticket.organizationName || 'N/A'}
        </span>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      className: 'w-[10%]',
      render: (ticket: SupportTicket) => getPriorityBadge(ticket.priority),
    },
    {
      key: 'status',
      header: 'Status',
      className: 'w-[10%]',
      render: (ticket: SupportTicket) => getStatusBadge(ticket.status),
    },
    {
      key: 'createdAt',
      header: 'Created',
      className: 'w-[10%]',
      render: (ticket: SupportTicket) => (
        <span className="text-[hsl(var(--muted-foreground))]">
          {ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString() : 'N/A'}
        </span>
      ),
    },
  ];

  const stats = ticketsResult?.summary || {
    total: 0,
    open: 0,
    inProgress: 0,
    resolved: 0,
    urgent: 0,
  };

  return (
    <div className="w-full space-y-3">
      <PageHeader
        title="Support Management"
        subtitle="Customer support tickets"
        subtitleVariant="description"
        subtitleAccent={false}
        className="mb-1"
        breadcrumb={[
          { label: 'Customers' },
          { label: 'Support', current: true },
        ]}
        actions={
          <>
            <button
              type="button"
              onClick={() => {
                setIsRefreshing(true);
                queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
                setTimeout(() => setIsRefreshing(false), 1000);
              }}
              disabled={isRefreshing}
              className={erpPageBtnSecondaryClass}
            >
              <MdRefresh className={cn('w-5 h-5', isRefreshing && 'animate-spin')} />
              Refresh
            </button>
            <button type="button" onClick={handleCreate} className={erpPageBtnPrimaryClass}>
              <MdAdd className="w-5 h-5" />
              New Ticket
            </button>
          </>
        }
      />

      {/* Summary Cards */}
      <div className={cn(erpStatGridClasses(5), '!gap-2')}>
        <div className={cn(erpStatCardClass, '!py-2.5')}>
          <div>
            <p className={erpStatCardLabelClass}>Total Tickets</p>
            <p className={cn(erpStatCardValueClass, '!text-xl')}>{stats.total}</p>
          </div>
          <div className={cn(erpStatCardIconClass, 'erp-stat-card-icon--blue !p-2')}>
            <MdSupport className="w-5 h-5" />
          </div>
        </div>
        <div className={cn(erpStatCardClass, '!py-2.5')}>
          <div>
            <p className={erpStatCardLabelClass}>Open</p>
            <p className={cn(erpStatCardValueClass, 'erp-stat-card-value--primary !text-xl')}>{stats.open}</p>
          </div>
          <div className={cn(erpStatCardIconClass, 'erp-stat-card-icon--cyan !p-2')}>
            <MdSchedule className="w-5 h-5" />
          </div>
        </div>
        <div className={cn(erpStatCardClass, '!py-2.5')}>
          <div>
            <p className={erpStatCardLabelClass}>In Progress</p>
            <p className={cn(erpStatCardValueClass, 'erp-stat-card-value--amber !text-xl')}>{stats.inProgress}</p>
          </div>
          <div className={cn(erpStatCardIconClass, 'erp-stat-card-icon--amber !p-2')}>
            <MdWarning className="w-5 h-5" />
          </div>
        </div>
        <div className={cn(erpStatCardClass, '!py-2.5')}>
          <div>
            <p className={erpStatCardLabelClass}>Resolved</p>
            <p className={cn(erpStatCardValueClass, 'erp-stat-card-value--emerald !text-xl')}>{stats.resolved}</p>
          </div>
          <div className={cn(erpStatCardIconClass, 'erp-stat-card-icon--emerald !p-2')}>
            <MdCheckCircle className="w-5 h-5" />
          </div>
        </div>
        <div className={cn(erpStatCardClass, '!py-2.5')}>
          <div>
            <p className={erpStatCardLabelClass}>Urgent</p>
            <p className={cn(erpStatCardValueClass, 'erp-stat-card-value--danger !text-xl')}>{stats.urgent}</p>
          </div>
          <div className={cn(erpStatCardIconClass, 'erp-stat-card-icon--danger !p-2')}>
            <MdWarning className="w-5 h-5" />
          </div>
        </div>
      </div>

      <div className={cn(erpSearchToolbarClass, '!gap-2 !rounded-lg !p-3')}>
        <div className={erpSearchToolbarFieldClass}>
          <MdSearch />
          <input
            type="text"
            placeholder="Search tickets by subject, description, or organization..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={onSearchKeyDown}
            className={erpSearchToolbarInputClass}
          />
        </div>

        <div className={erpSearchToolbarFiltersClass}>
          <div className={erpSearchToolbarFiltersLabelClass}>
            <MdFilterList className="w-5 h-5" />
            <span>Filters:</span>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className={erpSearchToolbarSelectClass}
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in-progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className={erpSearchToolbarSelectClass}
          >
            <option value="all">All Priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
          <ClearFiltersButton
            visible={!!searchQuery.trim() || filterStatus !== 'all' || filterPriority !== 'all'}
            onClick={() => {
              clearSearch();
              setFilterStatus('all');
              setFilterPriority('all');
            }}
            variant="toolbar"
          />
        </div>
      </div>

      {/* Support Tickets Table */}
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-baseline gap-2 min-w-0">
            <h2 className="text-sm font-semibold text-[hsl(var(--foreground))]">Support tickets</h2>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              {totalTickets} ticket{totalTickets !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsExportOpen(true)}
            className={erpPageBtnSecondaryClass}
          >
            <MdDownload className="w-4 h-4" />
            Export
          </button>
        </div>

        {isLoading ? (
          <PageSkeleton variant="table" />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={tickets}
              onEdit={handleEdit}
              onDelete={handleDelete}
              emptyMessage="No support tickets found."
              compact
              footer={
                totalTickets > 0 ? (
                  <Pagination
                    currentPage={page}
                    totalPages={Math.max(1, totalPages)}
                    onPageChange={setPage}
                    itemsPerPage={limit}
                    totalItems={totalTickets}
                    alwaysShow
                    onItemsPerPageChange={(nextLimit) => {
                      setLimit(nextLimit);
                      setPage(1);
                    }}
                  />
                ) : null
              }
            />
          </>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleClose}
        title={editingTicket ? 'Edit support ticket' : 'Create support ticket'}
        description={
          editingTicket
            ? 'Update ticket details, priority, and assignment.'
            : 'Log a customer issue. Required fields must be completed before creating the ticket.'
        }
        size="medium"
        compact
      >
        <SupportTicketForm
          ticket={editingTicket as any}
          onSuccess={handleSuccess}
          onCancel={handleClose}
        />
      </Modal>

      <ExportDataModal
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        onExport={async (format) => {
          try {
            await handleExport(format);
          } catch (error: any) {
            showToast(error.message || 'Failed to export tickets', 'error');
            throw error;
          }
        }}
        recordCount={totalTickets}
        title="Export tickets"
        entityLabel="ticket"
      />
    </div>
  );
}
